import React from "react";
import { motion } from "framer-motion";
import { KeyRound, Sparkles, Droplets, Wrench, Hammer, Truck, Check, ArrowRight } from "lucide-react";
import { SERVICES, buildWhatsAppLink } from "@/lib/constants";

const ICONS = {
  leasing: KeyRound,
  innenraum: Sparkles,
  aussen: Droplets,
  "smart-repair": Wrench,
  karosserie: Hammer,
  premium: Truck,
};

const Services = () => {
  return (
    <section id="leistungen" data-testid="services-section" className="py-24 lg:py-32 bg-[#F8FAFC]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#1FA3B5] mb-4">Unsere Leistungen</p>
          <h2 className="font-heading text-4xl lg:text-5xl font-bold text-slate-900 leading-tight">
            Alles für Ihre Leasingrückgabe –<br />
            <span className="text-[#1FA3B5]">aus einer Hand.</span>
          </h2>
          <p className="mt-6 text-lg text-slate-600 max-w-2xl">
            Von der Aufbereitung über Smart Repair bis zum Premium Hol- &amp; Bringdienst.
            Wir kümmern uns um alles, was sonst teuer wird.
          </p>
        </motion.div>

        <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.id] || KeyRound;
            const featured = s.featured;
            return (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                data-testid={`service-card-${s.id}`}
                className={`group relative rounded-3xl p-8 lg:p-10 transition-all duration-300 hover:-translate-y-1 ${
                  featured
                    ? "bg-gradient-to-br from-slate-900 to-[#0F4651] text-white shadow-2xl shadow-slate-900/20 lg:row-span-1 lg:col-span-1"
                    : "bg-white border border-slate-100 hover:shadow-2xl hover:shadow-slate-900/5"
                }`}
              >
                {s.badge && (
                  <span className={`inline-flex items-center text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full mb-5 ${
                    featured ? "bg-[#1FA3B5] text-white" : "bg-[#E6F6F8] text-[#0F7783]"
                  }`}>
                    {s.badge}
                  </span>
                )}

                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 transition-colors ${
                  featured
                    ? "bg-[#1FA3B5] text-white"
                    : "bg-[#E6F6F8] text-[#1FA3B5] group-hover:bg-[#1FA3B5] group-hover:text-white"
                }`}>
                  <Icon className="w-7 h-7" />
                </div>

                <h3 className={`font-heading text-2xl font-semibold leading-snug ${
                  featured ? "text-white" : "text-slate-900"
                }`}>
                  {s.title}
                </h3>
                <p className={`mt-4 leading-relaxed ${
                  featured ? "text-slate-300" : "text-slate-600"
                }`}>
                  {s.desc}
                </p>

                <ul className="mt-6 space-y-2.5">
                  {s.bullets.map((b) => (
                    <li key={b} className={`flex items-start gap-2.5 text-sm ${
                      featured ? "text-slate-200" : "text-slate-700"
                    }`}>
                      <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                        featured ? "text-[#5DD8E5]" : "text-[#1FA3B5]"
                      }`} />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href={buildWhatsAppLink(`Hallo AutoCoach, ich interessiere mich für: ${s.title}.`)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`service-cta-${s.id}`}
                  className={`mt-8 inline-flex items-center gap-2 font-semibold text-sm group/link ${
                    featured ? "text-[#5DD8E5]" : "text-[#1FA3B5]"
                  }`}
                >
                  Anfragen
                  <ArrowRight className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" />
                </a>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
